<template>
    <div class="relative">
        <div class="mx-auto flex w-full max-w-[1055px] flex-col items-center">
            <div
                class="group w-full rounded-[2rem] border border-transparent bg-dark p-3 text-center text-primary-lighter shadow-lg transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-300 hover:border-primary-medium hover:bg-primary-dark"
                :class="containerClass"
            >
                <label v-if="label" :for="id" class="mt-5 text-center text-lg font-medium text-primary-light italic">
                    {{ label }} <span v-if="required" class="text-red-600">*</span> :
                </label>
                <br aria-hidden="true" />
                <textarea
                    :id="id"
                    :value="modelValue"
                    @input="emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)"
                    @blur="emit('blur')"
                    :placeholder="placeholder"
                    :disabled="disabled"
                    :rows="rows"
                    :aria-describedby="describedByIds || undefined"
                    :aria-invalid="!!error"
                    :aria-label="label"
                    :class="textareaClass"
                    class="mt-5"
                ></textarea>
            </div>
            <p v-if="error" :id="`${id}-error`" class="mt-1 text-sm text-red-600">{{ error }}</p>
            <p v-if="hint" :id="`${id}-hint`" class="mt-1 text-xs text-gray-500">{{ hint }}</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

interface Props {
    modelValue: string;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    error?: string;
    showError?: boolean;
    rows?: number;
    hint?: string;
    baseClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
    rows: 6,
    baseClass:
        'w-full min-h-[180px] resize-y rounded-[1.5rem] bg-primary-light p-4 text-lg font-bold tracking-wide text-dark placeholder:text-dark/60 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
    (e: 'blur'): void;
}>();

const id = ref(`textarea-${Math.random().toString(36).substr(2, 9)}`);

const containerClass = computed(() => {
    if (!(props.showError && props.error)) {
        return '';
    }

    return 'border-red-500 hover:border-red-500 focus-within:ring-red-400';
});

const textareaClass = computed(() => {
    const hasError = props.showError && props.error;
    return hasError ? `${props.baseClass} bg-red-50 text-red-700 placeholder:text-red-400` : props.baseClass;
});

const describedByIds = computed(() => {
    const ids: string[] = [];
    if (props.error) ids.push(`${id.value}-error`);
    if (props.hint) ids.push(`${id.value}-hint`);
    return ids.join(' ');
});
</script>
